'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'

import { showErrorNotification } from '@/components/basic/notifications'
import {
  type EquityAssetSort,
  EquityScreenerAsset,
  fetchMostTradedStocks,
} from '@/services/equity/screener'

/**
 * Number of pages the user should always be able to step forward from the
 * current page. On mount we fetch 1 + PREFETCH_AHEAD pages worth of data.
 */
const PREFETCH_AHEAD = 3
const MAX_RETRY = 3

interface UseCursorPagedStocksResult {
  /** Records for the current page. Empty array during the initial load. */
  records: EquityScreenerAsset[]
  /** True on the very first load (no data yet). */
  isPending: boolean
  /** True whenever any chunk fetch is in flight (initial or prefetch). */
  isFetching: boolean
  hasMore: boolean
  /** Number of pages currently held in memory (each = page size rows). */
  loadedPageCount: number
  currentPage: number
  /**
   * Navigate to a page. If the target chunk is already in memory the switch
   * is instant; otherwise we fetch forward first and only switch when the
   * target page is actually available. On failure the user stays put.
   */
  gotoPage: (page: number) => Promise<void>
}

/**
 * Cursor-paged replacement for `useInfiniteQuery` on the stocks tab.
 *
 * Instead of an ever-growing flat list, we keep chunks in memory:
 *   chunks[0] → page 1 (rows 1..20)
 *   chunks[1] → page 2 (rows 21..40)
 *   ...
 *
 * When the user goes to page N, we make sure chunks[N-1 .. N+PREFETCH_AHEAD-1]
 * are loaded — fetching forward from `nextCursor` as many times as needed.
 * Already-loaded chunks are kept so flipping back is instant.
 *
 * Errors: each chunk fetch retries up to MAX_RETRY times. On final failure we
 * show one error toast and stop — already-loaded pages stay usable, and the
 * user stays on the current page instead of landing on an empty one.
 *
 * Unmount: any in-flight fetch is aborted, and post-abort callbacks no-op so
 * we don't setState on an unmounted component.
 *
 * Sort: the upstream cursor is only valid within one sort order. When `sort`
 * changes the hook aborts the in-flight run, clears all chunks and restarts
 * from page 1 — mirroring the unmount/remount cycle.
 */
export function useCursorPagedStocks(sort?: EquityAssetSort): UseCursorPagedStocksResult {
  const t = useTranslations('stockTable')
  const [chunks, setChunks] = useState<EquityScreenerAsset[][]>([])
  const [hasMore, setHasMore] = useState(true)
  // Initial mount always kicks off a fetch — start as true so the very first
  // render doesn't flash the EmptyState.
  const [isFetching, setIsFetching] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)

  // Identity-normalized sort (parent passes an object literal each render).
  // The ref mirrors the latest value so async loops always fetch with the
  // current sort without re-creating callbacks.
  const sortKey = sort ? `${sort.sortBy}:${sort.sortDirection}` : ''
  const sortRef = useRef(sort)
  sortRef.current = sort

  // Latest mirror of the data state so async loops see fresh values without
  // re-creating callbacks.
  const stateRef = useRef({
    chunks,
    hasMore,
    nextCursor: undefined as string | undefined,
    targetChunkCount: 1 + PREFETCH_AHEAD,
  })
  stateRef.current.chunks = chunks
  stateRef.current.hasMore = hasMore

  // Single-flight guard for the prefetch loop.
  const inFlightRef = useRef<Promise<boolean> | null>(null)

  // Tracks the current fetch's AbortController so we can cancel it on unmount.
  // A new controller is created per prefetch loop run; the ref always points
  // at the live one (or null when idle).
  const abortControllerRef = useRef<AbortController | null>(null)

  // Flipped on unmount so any promise callbacks that fire after cleanup
  // (e.g. an abort rejection being handled) skip setState.
  //
  // React 18 StrictMode mounts → unmounts → remounts in dev, so the cleanup
  // must leave the hook in a state where the next mount can start a fresh
  // fetch: clear inFlightRef so the follow-up prefetchUpTo isn't short-
  // circuited by the dying run's promise.
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      abortControllerRef.current?.abort()
      abortControllerRef.current = null
      inFlightRef.current = null
    }
  }, [])

  /**
   * Fetches chunks until `stateRef.current.chunks.length >= targetChunkCount`,
   * or the backend says no more. Re-entry while a loop is already running just
   * bumps the target — the loop re-reads it on each iteration, so a fast-click
   * "go to page 5" doesn't get swallowed by the in-flight "fill pages 1..4".
   *
   * Returns true when the loop ended with the target satisfied (or hasMore
   * exhausted naturally); false when it gave up on an error. The error toast
   * fires only after the whole loop gives up, not per-retry.
   */
  const prefetchUpTo = (targetChunkCount: number): Promise<boolean> => {
    stateRef.current.targetChunkCount = Math.max(
      stateRef.current.targetChunkCount,
      targetChunkCount,
    )

    if (inFlightRef.current !== null) return inFlightRef.current

    const controller = new AbortController()
    abortControllerRef.current = controller

    const run = async (): Promise<boolean> => {
      if (isMountedRef.current) setIsFetching(true)
      try {
        while (
          stateRef.current.chunks.length < stateRef.current.targetChunkCount &&
          stateRef.current.hasMore
        ) {
          let result: Awaited<ReturnType<typeof fetchMostTradedStocks>> | null = null
          let lastError: unknown = null
          for (let attempt = 0; attempt < MAX_RETRY; attempt += 1) {
            try {
              result = await fetchMostTradedStocks({
                pageToken: stateRef.current.nextCursor,
                sort: sortRef.current,
                abortController: controller,
              })
              break
            } catch (error) {
              // Aborted (unmount) — bail out quietly, no toast, no retry.
              if (controller.signal.aborted) return false
              lastError = error
            }
          }
          if (!result) throw lastError

          // Empty payload means the upstream is exhausted or returning junk —
          // stop rather than loop on a dead cursor.
          if (result.data.length === 0) {
            stateRef.current.hasMore = false
            if (isMountedRef.current) setHasMore(false)
            break
          }

          // Defensive: if the backend says has_more but gives no cursor, we'd
          // loop forever re-fetching the same first page. Treat it as the end.
          const nextCursor = result.pagination.next_page_token ?? undefined
          const hasMoreChunks = result.pagination.has_more && !!nextCursor

          const newChunks = [...stateRef.current.chunks, result.data]
          stateRef.current.chunks = newChunks
          stateRef.current.hasMore = hasMoreChunks
          stateRef.current.nextCursor = nextCursor

          if (isMountedRef.current) {
            setChunks(newChunks)
            setHasMore(hasMoreChunks)
          }
        }
        return true
      } catch {
        if (isMountedRef.current && !controller.signal.aborted) {
          showErrorNotification({
            title: t('loadErrorTitle'),
            message: t('loadErrorMessage'),
          })
        }
        return false
      } finally {
        // An aborted run must not touch isFetching: on StrictMode remount the
        // follow-up run already claimed it (set true), and this dying run
        // setting it false would flash the EmptyState while chunks are empty.
        if (isMountedRef.current && !controller.signal.aborted) setIsFetching(false)
        // Only clear the abort controller if it's still ours — a re-entry
        // could have started a new loop with its own controller.
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null
        }
      }
    }

    // Single-flight bookkeeping. The assignment MUST happen before the .finally
    // clears the ref — if `run()`'s synchronous prelude completes without
    // hitting an await (e.g. the while-loop is already satisfied), the function
    // returns an already-resolved promise, and an inline `inFlightRef.current
    // = null` inside run()'s own finally would have executed before this
    // assignment, leaving the resolved promise pinned in the ref forever.
    // Clear via the promise's own .finally with an ownership check instead.
    const promise = run()
    inFlightRef.current = promise
    promise.finally(() => {
      if (inFlightRef.current === promise) inFlightRef.current = null
    })
    return promise
  }

  /**
   * Navigate to a page. If the target chunk (and the PREFETCH_AHEAD runway
   * after it) is already in memory the switch is instant and we fetch nothing
   * — only a genuine shortfall triggers a fetch. Uncached target → fetch
   * first, switch only if we actually got the target page's data. On failure
   * the user stays on the current page.
   */
  const gotoPage = async (targetPage: number) => {
    if (targetPage === currentPage) return
    const targetChunkCount = targetPage + PREFETCH_AHEAD
    if (
      stateRef.current.chunks[targetPage - 1] &&
      stateRef.current.chunks.length >= targetChunkCount
    ) {
      // Target + its prefetch runway are both already loaded → instant switch,
      // no extra request. (Initial load and every shortfall below already top
      // the pool up to target+PREFETCH_AHEAD, so a fully-covered page keeps
      // its runway warm without re-fetching.)
      setCurrentPage(targetPage)
      return
    }

    // Shortfall (target page itself, or its runway) — fetch and switch only
    // on success.
    const ok = await prefetchUpTo(targetChunkCount)
    if (!ok) {
      // prefetchUpTo already showed the generic load-error toast. The user
      // just stays on the current page — no extra UI here.
      return
    }
    if (stateRef.current.chunks[targetPage - 1]) {
      setCurrentPage(targetPage)
    }
  }

  // Initial load: fill pages 1..1+PREFETCH_AHEAD so the user starts on page 1
  // with PREFETCH_AHEAD pages ahead ready. Re-runs when the sort changes:
  // upstream cursors are sort-scoped, so the in-flight run is aborted and all
  // accumulated state is cleared before restarting from page 1.
  useEffect(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    inFlightRef.current = null

    stateRef.current.chunks = []
    stateRef.current.hasMore = true
    stateRef.current.nextCursor = undefined
    stateRef.current.targetChunkCount = 1 + PREFETCH_AHEAD

    setChunks([])
    setHasMore(true)
    setIsFetching(true)
    setCurrentPage(1)

    void prefetchUpTo(1 + PREFETCH_AHEAD)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey])

  const records = chunks[currentPage - 1] ?? []
  const isPending = chunks.length === 0 && isFetching

  return {
    records,
    isPending,
    isFetching,
    hasMore,
    loadedPageCount: chunks.length,
    currentPage,
    gotoPage,
  }
}
